import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import moment from "moment";
import { previewScheduleChangeApi, saveScheduleChangeApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, InfoHint, Pill, WEEKDAYS, WEEKDAY_SHORT } from "./cwUi";

/**
 * P3.8 — change one client's check-in day (design PDF page 6, same layout:
 * day buttons + time, numbered 01/02/03 preview, reason, "What changes").
 * Shows the preview first (current window keeps its dates → one bridging
 * window → new day from then on). Only when the admin turned on
 * "Coaches can change a client's check-in day" for the journey.
 */

function WindowRow({ n, title, win, tone, note }) {
  if (!win) return null;
  const bg = tone === "bridge" ? "#fff1e6" : "#e6f6f9";
  return (
    <Box sx={{ display: "flex", gap: 2.5, p: { xs: 1.75, md: 2.25 }, borderRadius: "10px", bgcolor: bg }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: CW.primary, pt: 0.25 }}>{n}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 700, color: CW.ink }}>{title}</Typography>
        <Typography sx={{ fontSize: { xs: 14, md: 15.5 }, fontWeight: 600, color: CW.ink, mt: 0.5 }}>
          {win.opensLabel}
          {win.closesLabel ? ` → ${win.closesLabel}` : ""}
        </Typography>
        {note ? <Typography sx={{ fontSize: 13, color: CW.muted, mt: 0.75 }}>{note}</Typography> : null}
      </Box>
    </Box>
  );
}

const WHAT_CHANGES = [
  ["One enrolment", "The client stays in the same programme and uses the same check-in questions."],
  ["Future automations", "Schedule-based reminders and triggers use the client's own dates."],
  ["A visible transition", "The current deadline is protected. The bridging window is shown clearly."],
  ["A separate coach obligation", "Existing submissions, checklist work and reply deadlines stay attached."],
];


/**
 * DIALOG-CLOSE (3 Nov): close only with ✕ / Cancel. A click outside, or the
 * Escape / Cancel of the browser's own time or date picker, used to close
 * this dialog too (the coach saw two popups disappear at once).
 */
const safeClose = (onClose, busy) => (event, reason) => {
  if (busy || reason === "backdropClick") return;
  if (reason === "escapeKeyDown") {
    const el = typeof document !== "undefined" ? document.activeElement : null;
    if (el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
  }
  if (onClose) onClose();
};

export default function ScheduleChangeDialog({ open, onClose, trackerId, personKey, memberId, personName, trackerName, current, rules, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [weekday, setWeekday] = useState("");
  const [time, setTime] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWeekday("");
    setTime(current?.time || "");
    setReason("");
    setPreview(null);
    setError("");
  }, [open, current]);

  useEffect(() => {
    if (!open || weekday === "") return undefined;
    let live = true;
    setBusy(true);
    setError("");
    previewScheduleChangeApi({ trackerId, personKey, memberId, weekday: Number(weekday), time }).then((result) => {
      if (!live) return;
      if (result?.code === 200) setPreview(result.preview);
      else {
        setPreview(null);
        setError(result?.message || "Could not build the preview");
      }
      setBusy(false);
    });
    return () => {
      live = false;
    };
  }, [open, weekday, time, trackerId, personKey, memberId]);

  const save = async () => {
    setBusy(true);
    const result = await saveScheduleChangeApi({
      trackerId,
      personKey,
      memberId,
      weekday: Number(weekday),
      time,
      reason,
    });
    setBusy(false);
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Check-in day changed", { variant: "success" });
      if (onSaved) onSaved(result);
      onClose();
    } else setError(result?.message || "Could not save");
  };

  const allowed = rules?.allowCheckInDayChange && rules?.dayChangeSupported !== false;
  const order = [1, 2, 3, 4, 5, 6, 0];
  const isCurrent = (i) => (current?.weekdays || []).length === 1 && current.weekdays[0] === i;
  const tz = rules?.checkInTime ? rules?.checkInTimeZone || "" : "";

  return (
    <Dialog
      open={open}
      onClose={safeClose(onClose, busy)}
      fullWidth
      maxWidth={allowed ? "lg" : "sm"}
      PaperProps={{ sx: { bgcolor: CW.soft, borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pb: 0.5, pr: 7, position: "relative" }}>
        <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: CW.ink }}>
          Change check-in day{personName ? ` · ${personName}` : ""}
          <InfoHint id="changeday.main" />
        </Typography>
        <Typography sx={{ fontSize: 14.5, color: CW.muted }}>You see the exact change before saving.</Typography>
        <IconButton
          aria-label="Close"
          data-testid="dialog-close"
          onClick={busy ? undefined : onClose}
          disabled={busy}
          sx={{ position: "absolute", right: 12, top: 12, color: CW.muted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "16px !important" }}>
        {!allowed ? (
          <Alert severity="info">
            {rules?.dayChangeSupported === false
              ? "Day change works for weekly check-ins only."
              : "Changing a client's check-in day is turned off. An admin can turn it on in Check-in settings."}
          </Alert>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.6fr) minmax(0, 1fr)" }, gap: 3, alignItems: "start" }}>
            <Box sx={{ bgcolor: "#fff", border: `1px solid ${CW.line}`, borderRadius: "14px", p: { xs: 2, md: 3 }, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }}>
                <Box>
                  <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 700, color: CW.ink }}>{personName || "Client"}</Typography>
                  <Typography sx={{ fontSize: 14.5, color: CW.muted }}>
                    {[trackerName, rules?.cadenceLabel].filter(Boolean).join(" / ")}
                  </Typography>
                </Box>
                <Pill tone="teal" sx={{ fontSize: 12.5, px: 1.75, py: 0.6 }}>
                  This enrolment only
                </Pill>
              </Box>
              <Box sx={{ borderTop: `1px solid ${CW.line}`, mt: 2.5, pt: 2.5 }}>
                <Typography sx={{ fontSize: 13.5, color: CW.muted, fontWeight: 600 }}>Current schedule</Typography>
                <Typography sx={{ fontSize: { xs: 17, md: 20 }, fontWeight: 700, color: CW.ink, mt: 0.5 }}>
                  {current?.label || "—"}
                  {tz ? ` · ${tz}` : ""}
                  {current?.source === "person" ? " (own day)" : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-end", mt: 2.5 }}>
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mb: 1 }}>New check-in day</Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }} role="radiogroup" aria-label="New day">
                    {order.map((i) => {
                      const on = String(weekday) === String(i);
                      return (
                        <Box
                          key={i}
                          component="button"
                          type="button"
                          role="radio"
                          aria-checked={on}
                          disabled={isCurrent(i)}
                          onClick={() => setWeekday(i)}
                          title={isCurrent(i) ? "Current day" : WEEKDAYS[i]}
                          sx={{
                            font: "inherit",
                            minWidth: 64,
                            py: 1.25,
                            px: 1.5,
                            borderRadius: "10px",
                            border: `1.5px solid ${on ? CW.primary : CW.line}`,
                            bgcolor: on ? CW.tealSoft : "#fff",
                            color: on ? CW.primary : CW.ink,
                            fontWeight: on ? 700 : 500,
                            fontSize: 15,
                            cursor: "pointer",
                            "&:disabled": { opacity: 0.4, cursor: "default" },
                          }}
                        >
                          {WEEKDAY_SHORT[i]}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                {rules?.checkInTime ? (
                  <Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mb: 1 }}>Time</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <TextField
                        size="small"
                        type="time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        inputProps={{ "aria-label": "Opens at" }}
                        sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                      />
                      {tz ? <Typography sx={{ fontSize: 13, color: CW.muted }}>{tz} time</Typography> : null}
                    </Box>
                  </Box>
                ) : null}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 3, mb: 1.5 }}>
                <Typography sx={{ fontSize: { xs: 18, md: 21 }, fontWeight: 700, color: CW.ink }}>Schedule preview</Typography>
                <Typography sx={{ fontSize: 12.5, color: CW.muted }}>Requested {moment().format("D MMM YYYY")}</Typography>
              </Box>
              {weekday === "" ? (
                <Typography sx={{ fontSize: 13.5, color: CW.muted, border: `1px dashed ${CW.line}`, borderRadius: "10px", p: 2 }}>
                  Pick a new day to see the transition.
                </Typography>
              ) : null}
              {busy && !preview ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : null}
              {error ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {error}
                </Alert>
              ) : null}
              {preview ? (
                <Box sx={{ display: "grid", gap: 1.5 }}>
                  <WindowRow
                    n="01"
                    title="Current window stays unchanged"
                    win={preview.current}
                    note="Already open or submitted; its review deadline is unchanged."
                  />
                  <WindowRow
                    n="02"
                    title={`Transition window${preview.transition?.days != null ? ` · ${preview.transition.days} day${preview.transition.days === 1 ? "" : "s"}` : ""}`}
                    win={preview.transition}
                    tone="bridge"
                    note={`This window lines up the next check-in with ${preview.weekdayName}.`}
                  />
                  <WindowRow
                    n={preview.transition ? "03" : "02"}
                    title={`${preview.weekdayName} schedule continues`}
                    win={preview.continues}
                    note={`Every ${preview.weekdayName} after this, using the same check-in.`}
                  />
                  {!preview.continues ? (
                    <Alert severity="info" data-testid="no-new-window">
                      This journey ends before the next {preview.weekdayName} check-in, so there is nothing to move. Pick another day or keep the current one.
                    </Alert>
                  ) : null}
                </Box>
              ) : null}

              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mt: 3, mb: 0.75 }}>Reason (optional)</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Spread weekly coaching workload"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                inputProps={{ maxLength: 500, "aria-label": "Reason (optional, coach only)" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: CW.soft } }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 3, flexWrap: "wrap" }}>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: CW.ink }}>Future reminders follow this schedule.</Typography>
                  <Typography sx={{ fontSize: 12.5, color: CW.muted }}>Past check-ins and the open window keep their dates.</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <button type="button" className="small-contained-outlined-button" style={{ padding: "10px 26px", color: CW.ink, borderColor: CW.line }} onClick={onClose} disabled={busy}>
                    Cancel
                  </button>
                  <button type="button" className="small-contained-button" style={{ padding: "10px 30px", minWidth: 190 }} onClick={save} disabled={busy || !preview || !preview.continues}>
                    Save schedule
                  </button>
                </Box>
              </Box>
            </Box>

            <Box sx={{ minWidth: 0, pt: { md: 2 } }}>
              <Typography sx={{ fontSize: { xs: 19, md: 23 }, fontWeight: 700, color: CW.ink, mb: 2.5 }}>What changes</Typography>
              {WHAT_CHANGES.map(([title, text], idx) => (
                <Box key={title} sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: CW.primary, pt: 0.25 }}>{`0${idx + 1}`}</Typography>
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: CW.ink }}>{title}</Typography>
                    <Typography sx={{ fontSize: 13.5, color: CW.muted, mt: 0.5, lineHeight: 1.6 }}>{text}</Typography>
                  </Box>
                </Box>
              ))}
              <Box sx={{ bgcolor: "#1f2a33", color: "#fff", borderRadius: "14px", p: 2.5, mt: 1 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Transition policy</Typography>
                <Typography sx={{ fontSize: 13.5, mt: 1, lineHeight: 1.7, color: "#d6dde3" }}>
                  Preserve and bridge: the current window keeps its dates, one shorter window bridges to the new day, and
                  reminders follow the client's own schedule.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      {!allowed ? (
        <Box sx={{ px: 3, pb: 2, display: "flex", justifyContent: "flex-end" }}>
          <button type="button" className="small-contained-outlined-button" onClick={onClose}>
            Close
          </button>
        </Box>
      ) : null}
    </Dialog>
  );
}
